import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { ArrowLeft, Save, X, Upload, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { motion } from 'framer-motion';
import { blogs } from '../../data/mock';
import RichTextEditor from '../ui/RichTextEditor';
import PageHeader from '../admin/PageHeader';
import ImageUploader from '../ui/ImageUploader';
import { fetchBlogById, editBlog } from '@/hooks/useBlog';

const EditBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    tags: [],
    images: [],
    mediumURL: "",
    featured: false,
    readTime: "",
  });
  const [newTag, setNewTag] = useState("");

  // Fetch blog details on mount
  useEffect(() => {
    const loadBlog = async () => {
      try {
        const res = await fetchBlogById(id);
        const blog = res.blog || res; // adjust if API shape is different

        setFormData({
          title: blog.title || "",
          excerpt: blog.excerpt || "",
          content: blog.content || "",
          tags: blog.tags || [],
          images: Array.isArray(blog.images) ? blog.images : blog.image ? [blog.image] : [],
          mediumURL: blog.mediumURL || "",
          featured: blog.featured || false,
          readTime: blog.readTime || "",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load blog details.",
          variant: "destructive",
        });
      }
    };
    loadBlog();
  }, [id, toast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContentChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
    }
  };

  const removeTag = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const dataToSend = new FormData();
      dataToSend.append("title", formData.title);
      dataToSend.append("excerpt", formData.excerpt);
      dataToSend.append("content", formData.content);
      dataToSend.append("mediumURL", formData.mediumURL);
      dataToSend.append("featured", formData.featured);
      dataToSend.append("readTime", formData.readTime);
      formData.tags.forEach((tag) => dataToSend.append("tags[]", tag));
      formData.images.forEach((file) => dataToSend.append("images", file)); // multiple

      await editBlog(id, dataToSend);

      toast({
        title: "Blog Post Updated!",
        description: "Your blog post has been successfully updated.",
      });

      navigate("/admin/blogs");
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update blog post.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <PageHeader
        title="Edit Blog Post"
        description="Update your blog post"
        routeTo="/admin/blogs"
        backTo="Blog Posts"
      />

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Details */}
            <Card>
              <CardHeader>
                <CardTitle>Blog Post Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Label>Post Title *</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter blog post title"
                  required
                />

                <Label>Excerpt *</Label>
                <Textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  placeholder="Brief description..."
                  rows={3}
                  required
                />

                <Label>Read Time</Label>
                <Input
                  name="readTime"
                  value={formData.readTime}
                  onChange={handleChange}
                  placeholder="5 min read"
                />

                <Label>Medium URL (Optional)</Label>
                <div className="relative">
                  <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    name="mediumURL"
                    value={formData.mediumURL}
                    onChange={handleChange}
                    placeholder="https://medium.com/@username/article"
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Content */}
            <Card>
              <CardHeader>
                <CardTitle>Content *</CardTitle>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  value={formData.content}
                  onChange={handleContentChange}
                />
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    onKeyDown={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addTag())
                    }
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {tag}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-red-500"
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Images */}
            <Card>
              <CardHeader>
                <CardTitle>Featured Images</CardTitle>
              </CardHeader>
              <CardContent>
                <ImageUploader
                  multiple
                  value={formData.images}
                  onChange={(files) =>
                    setFormData((prev) => ({ ...prev, images: files }))
                  }
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Post Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <Label htmlFor="featured">Featured Post</Label>
                  <Switch
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, featured: checked }))
                    }
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Post"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/admin/blogs")}
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </motion.div>
  );
};


export default EditBlog;
